import React, { useEffect, useState } from 'react';
import { Edit2, Save, RotateCcw, Sparkles } from 'lucide-react';
import { reachPlaybookAPI, ReachPlaybookEntry } from '../../../services/reachService';
import { REACHStageBadge } from '../common/REACHStageBadge';

interface Props {
  projectId: string;
  week: number;
}

const FIELDS: Array<{ key: keyof ReachPlaybookEntry; label: string }> = [
  { key: 'cmActivities', label: 'CM Activities' },
  { key: 'comms', label: 'Communications' },
  { key: 'whatToNotice', label: 'What to Notice' },
  { key: 'sponsorAsks', label: 'Sponsor Asks' },
  { key: 'guardrail', label: 'Guardrail' },
];

export const PlaybookTab: React.FC<Props> = ({ projectId, week }) => {
  const [entry, setEntry] = useState<ReachPlaybookEntry | null>(null);
  const [draft, setDraft] = useState<Partial<ReachPlaybookEntry>>({});
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);

  useEffect(() => {
    setEditing(false);
    reachPlaybookAPI.getWeek(projectId, week).then(r => {
      setEntry(r.data.entry ?? null);
      setDraft(r.data.entry ?? {});
    });
  }, [projectId, week]);

  const save = async () => {
    setSaving(true);
    try {
      const payload: ReachPlaybookEntry = {
        week,
        reachStage: (draft.reachStage ?? entry?.reachStage ?? 'R') as ReachPlaybookEntry['reachStage'],
        cmActivities: draft.cmActivities ?? '',
        comms: draft.comms ?? '',
        whatToNotice: draft.whatToNotice ?? '',
        sponsorAsks: draft.sponsorAsks ?? '',
        guardrail: draft.guardrail ?? '',
        isCustomised: true,
      };
      await reachPlaybookAPI.putWeek(projectId, week, payload);
      setEntry(payload);
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    if (!window.confirm('Reset this week to default content?')) return;
    setResetting(true);
    try {
      await reachPlaybookAPI.resetWeek(projectId, week);
      const r = await reachPlaybookAPI.getWeek(projectId, week);
      setEntry(r.data.entry ?? null);
      setDraft(r.data.entry ?? {});
      setEditing(false);
    } finally {
      setResetting(false);
    }
  };

  if (!entry) return <div className="py-12 text-center text-slate-400 text-sm">Loading…</div>;

  return (
    <div className="flex flex-col gap-4">
      {/* Header bar */}
      <div className="flex items-center gap-3 flex-wrap">
        <REACHStageBadge stage={entry.reachStage} />
        {entry.isCustomised ? (
          <span className="flex items-center gap-1 text-xs text-primary font-semibold">
            <Sparkles size={12} /> Customised
          </span>
        ) : (
          <span className="text-xs text-slate-400 font-medium">Default content</span>
        )}
        <div className="ml-auto flex items-center gap-2">
          {entry.isCustomised && (
            <button
              onClick={reset}
              disabled={resetting}
              className="flex items-center gap-1.5 rounded-xl h-9 px-3 border border-slate-200 bg-white text-slate-500 text-xs font-semibold hover:bg-slate-50 transition-colors disabled:opacity-60"
            >
              <RotateCcw size={13} /> {resetting ? 'Resetting…' : 'Reset to default'}
            </button>
          )}
          {!editing ? (
            <button
              onClick={() => { setDraft({ ...entry }); setEditing(true); }}
              className="flex items-center gap-1.5 rounded-xl h-9 px-4 bg-primary hover:bg-primary-dark text-white text-xs font-bold shadow-sm transition-colors"
            >
              <Edit2 size={13} /> Edit week
            </button>
          ) : (
            <button
              onClick={save}
              disabled={saving}
              className="flex items-center gap-1.5 rounded-xl h-9 px-4 bg-primary hover:bg-primary-dark text-white text-xs font-bold shadow-sm transition-colors disabled:opacity-60"
            >
              <Save size={13} /> {saving ? 'Saving…' : 'Save changes'}
            </button>
          )}
          {editing && (
            <button
              onClick={() => { setDraft({ ...entry }); setEditing(false); }}
              className="flex items-center gap-1 rounded-xl h-9 px-3 border border-slate-200 bg-white text-slate-500 text-xs font-semibold hover:bg-slate-50 transition-colors"
            >
              Cancel
            </button>
          )}
        </div>
      </div>

      {/* Fields */}
      <div className="bg-white border border-[#e2e8f0] rounded-xl overflow-hidden divide-y divide-[#e2e8f0]">
        {FIELDS.map(({ key, label }) => (
          <div key={key} className="p-4">
            <p className="text-[11px] font-bold text-primary tracking-widest uppercase mb-2">{label}</p>
            {editing ? (
              <textarea
                rows={3}
                value={(draft[key] as string) ?? ''}
                onChange={e => setDraft(prev => ({ ...prev, [key]: e.target.value }))}
                className="w-full border border-[#e2e8f0] rounded-xl px-3 py-2.5 text-sm text-navy placeholder:text-slate-400 focus:ring-2 focus:ring-primary focus:border-transparent outline-none resize-none"
              />
            ) : (
              <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-line">
                {(entry[key] as string) || <span className="text-slate-400 italic">Not set</span>}
              </p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
